import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Lock, ShieldCheck, CheckCircle } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const FREQ_OPTIONS = [
  { value: 'hourly', label: 'Every hour' },
  { value: 'every_6h', label: 'Every 6 hours' },
  { value: 'daily', label: 'Daily (default)' },
  { value: 'weekly', label: 'Weekly' },
];

function formatTs(d) {
  if (!d) return '\u2014';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

/* ── Iterative schema flattener (avoids recursive JSX that crashes babel) ── */
function flattenSchemaForDisplay(schema, depth) {
  if (!schema || depth > 5) return [];
  var nodes = [];
  var t = schema._type;

  if (t === 'object' && schema._fields) {
    Object.entries(schema._fields).forEach(function (entry) {
      var k = entry[0], v = entry[1];
      nodes.push({ id: k + '_' + depth, pad: depth * 16 + 8, name: k, type: v._type });
      if (v._type === 'object' || v._type === 'array') {
        nodes = nodes.concat(flattenSchemaForDisplay(v, depth + 1));
      }
    });
  } else if (t === 'array' && schema._element_schema) {
    nodes.push({ id: 'arr_' + depth, pad: depth * 16 + 8, name: null, type: '[' + (schema._element_type || 'unknown') + ']' });
    nodes = nodes.concat(flattenSchemaForDisplay(schema._element_schema, depth + 1));
  }
  return nodes;
}

function SchemaTreeView({ schema }) {
  if (!schema) return null;
  var nodes = flattenSchemaForDisplay(schema, 0);
  if (nodes.length === 0) return <p className="text-xs text-kova-slate font-mono">Empty schema</p>;
  return (
    <div>
      {nodes.map(function (node, i) {
        return (
          <div key={node.id + '_' + i} style={{ paddingLeft: node.pad }} className="text-[10px] font-mono flex items-center gap-2 py-px">
            {node.name ? <span className="text-kova-cyan">{node.name}</span> : null}
            <span className="text-kova-slate">{node.name ? ': ' : ''}{node.type}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Diff viewer ── */
function DiffViewer({ diff }) {
  if (!diff || diff.total_changes === 0) {
    return <p className="text-xs text-kova-slate font-mono py-2">No changes detected</p>;
  }

  var items = [];
  (diff.removed_fields || []).forEach(function (f, i) {
    items.push({ key: 'rem_' + i, label: 'REMOVED', color: '#E24B4A', path: f.path, extra: f.type, testId: 'diff-removed-' + i });
  });
  (diff.type_changes || []).forEach(function (f, i) {
    items.push({ key: 'type_' + i, label: 'TYPE', color: '#C8A030', path: f.path, extra: f.old_type + ' \u2192 ' + f.new_type, testId: 'diff-type-' + i });
  });
  (diff.renamed_fields || []).forEach(function (f, i) {
    items.push({ key: 'ren_' + i, label: 'RENAME?', color: '#C8A030', path: f.old_path + ' \u2192 ' + f.new_path, extra: '', testId: 'diff-renamed-' + i, dimmed: true });
  });
  (diff.added_fields || []).forEach(function (f, i) {
    items.push({ key: 'add_' + i, label: 'ADDED', color: '#34D399', path: f.path, extra: f.type, testId: 'diff-added-' + i });
  });

  return (
    <div className="space-y-2" data-testid="diff-viewer">
      {items.map(function (it) {
        var bgAlpha = it.dimmed ? '04' : '08';
        return (
          <div key={it.key} className="flex items-center gap-2 px-3 py-1.5" style={{ borderLeft: '2px solid ' + it.color, background: it.color + bgAlpha }} data-testid={it.testId}>
            <span className="text-[10px] font-mono font-bold" style={{ color: it.color }}>{it.label}</span>
            <span className="text-[11px] font-mono" style={{ color: it.color }}>{it.path}</span>
            {it.extra ? <span className="text-[9px] font-mono ml-auto" style={{ color: it.color + '99' }}>{it.extra}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════
   MAIN PAGE
   ═══════════════════════════════ */
export default function ConnectionDetail() {
  var params = useParams();
  var connectionId = params.connectionId;
  var navigate = useNavigate();
  var [connection, setConnection] = useState(null);
  var [monitorConfig, setMonitorConfig] = useState(null);
  var [snapshots, setSnapshots] = useState([]);
  var [loading, setLoading] = useState(true);
  var [enabling, setEnabling] = useState(false);
  var [snapshotting, setSnapshotting] = useState(false);
  var [selectedSnapshot, setSelectedSnapshot] = useState(null);
  var [tab, setTab] = useState('overview');

  var fetchAll = useCallback(function () {
    setLoading(true);
    Promise.all([
      fetch(API + '/connections', { credentials: 'include' }),
      fetch(API + '/connections/' + connectionId + '/schema/config', { credentials: 'include' }),
      fetch(API + '/connections/' + connectionId + '/schema/snapshots?limit=30', { credentials: 'include' }),
    ]).then(function (responses) {
      var connRes = responses[0], configRes = responses[1], snapRes = responses[2];
      var promises = [];
      promises.push(connRes.ok ? connRes.json() : Promise.resolve(null));
      promises.push(configRes.ok ? configRes.json() : Promise.resolve(null));
      promises.push(snapRes.ok ? snapRes.json() : Promise.resolve([]));
      return Promise.all(promises);
    }).then(function (data) {
      if (data[0]) {
        var conn = data[0].find(function (c) { return c.connection_id === connectionId; });
        setConnection(conn || null);
      }
      if (data[1]) setMonitorConfig(data[1]);
      setSnapshots(data[2] || []);
    }).catch(function (e) {
      console.error(e);
    }).finally(function () {
      setLoading(false);
    });
  }, [connectionId]);

  useEffect(function () { fetchAll(); }, [fetchAll]);

  function toggleMonitoring() {
    if (!connection) return;
    setEnabling(true);
    var p;
    if (monitorConfig && monitorConfig.is_enabled) {
      p = fetch(API + '/connections/' + connectionId + '/schema/disable', { method: 'POST', credentials: 'include' })
        .then(function (res) {
          if (res.ok) { setMonitorConfig(function (prev) { return Object.assign({}, prev, { is_enabled: false }); }); toast.success('Monitoring disabled'); }
        });
    } else {
      p = fetch(API + '/connections/' + connectionId + '/schema/enable', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ endpoint: connection.base_url, frequency: 'daily' }),
      }).then(function (res) {
        if (res.ok) return res.json();
        return res.json().then(function (e) { toast.error(e.detail || 'Failed'); return null; });
      }).then(function (data) {
        if (!data) return;
        setMonitorConfig(data.config);
        if (data.baseline_snapshot) setSnapshots(function (prev) { return [data.baseline_snapshot].concat(prev); });
        toast.success('Monitoring enabled \u2014 baseline snapshot taken');
      });
    }
    p.catch(function () { toast.error('Error toggling monitoring'); })
      .finally(function () { setEnabling(false); });
  }

  function updateFrequency(freq) {
    fetch(API + '/connections/' + connectionId + '/schema/config', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ frequency: freq }),
    }).then(function (res) {
      if (res.ok) return res.json();
      return null;
    }).then(function (data) {
      if (data) { setMonitorConfig(data); toast.success('Frequency updated to ' + freq); }
    }).catch(function () { toast.error('Failed to update'); });
  }

  function takeManualSnapshot() {
    setSnapshotting(true);
    fetch(API + '/connections/' + connectionId + '/schema/snapshot', { method: 'POST', credentials: 'include' })
      .then(function (res) {
        if (res.ok) return res.json();
        return res.json().then(function (e) { toast.error(e.detail || 'Snapshot failed'); return null; });
      }).then(function (snap) {
        if (snap) { setSnapshots(function (prev) { return [snap].concat(prev); }); toast.success('Snapshot taken'); }
      }).catch(function () { toast.error('Snapshot failed'); })
      .finally(function () { setSnapshotting(false); });
  }

  function acknowledgeSnapshot(snapshotId) {
    fetch(API + '/schema/snapshots/' + snapshotId + '/acknowledge', { method: 'PUT', credentials: 'include' })
      .then(function (res) {
        if (res.ok) {
          var now = new Date().toISOString();
          setSnapshots(function (prev) {
            return prev.map(function (s) {
              return s.snapshot_id === snapshotId ? Object.assign({}, s, { acknowledged_by: 'me', acknowledged_at: now }) : s;
            });
          });
          setSelectedSnapshot(function (prev) {
            if (prev && prev.snapshot_id === snapshotId) return Object.assign({}, prev, { acknowledged_by: 'me', acknowledged_at: now });
            return prev;
          });
          toast.success('Acknowledged');
        }
      }).catch(function () { toast.error('Failed to acknowledge'); });
  }

  /* ── Render ── */
  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="font-mono text-kova-cyan">LOADING...</span></div>;
  }
  if (!connection) {
    return (
      <div className="text-center py-12">
        <p className="text-kova-slate">Connection not found</p>
        <Button onClick={function () { navigate('/connections'); }} variant="outline" className="mt-4 rounded-none border-kova-cyan/12">Back to Connections</Button>
      </div>
    );
  }

  var isEnabled = monitorConfig && monitorConfig.is_enabled;
  var hasUnacked = snapshots.some(function (s) { return s.has_breaking_changes && !s.acknowledged_by; });

  return (
    <div className="space-y-4" data-testid="connection-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={function () { navigate('/connections'); }} className="text-kova-slate hover:text-white transition-colors" data-testid="back-btn"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-heading font-bold text-lg">{connection.name}</h1>
            <span className={'text-[10px] font-mono font-bold ' + (connection.method === 'GET' ? 'text-kova-green' : 'text-kova-cyan')}>{connection.method}</span>
            {connection.auth_type !== 'none' ? <Lock size={12} className="text-kova-amber" /> : null}
            <div className={'w-2 h-2 rounded-full ' + (connection.is_active ? 'bg-kova-green' : 'bg-kova-slate')} />
          </div>
          <p className="text-[10px] font-mono text-kova-slate mt-0.5">{connection.base_url}</p>
        </div>
        <Button onClick={function () { navigate('/executor/' + connectionId); }} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-xs" data-testid="execute-btn">
          <Play size={14} weight="fill" className="mr-1" />Execute
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-kova-cyan/12">
        <button
          onClick={function () { setTab('overview'); }}
          className={'px-5 py-3 text-xs font-mono uppercase tracking-[2px] border-b-2 transition-colors ' + (tab === 'overview' ? 'text-kova-cyan border-kova-cyan' : 'text-kova-slate border-transparent hover:text-kova-silv')}
          data-testid="tab-overview"
        >Overview</button>
        <button
          onClick={function () { setTab('schema'); }}
          className={'px-5 py-3 text-xs font-mono uppercase tracking-[2px] border-b-2 transition-colors flex items-center gap-2 ' + (tab === 'schema' ? 'text-kova-cyan border-kova-cyan' : 'text-kova-slate border-transparent hover:text-kova-silv')}
          data-testid="tab-schema"
        >
          Schema History
          {hasUnacked ? <span className="w-2 h-2 rounded-full bg-kova-red animate-pulse" /> : null}
        </button>
      </div>

      {/* Tab content */}
      <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
        {tab === 'overview' ? renderOverview(connection, monitorConfig, isEnabled, enabling, toggleMonitoring, updateFrequency) : null}
        {tab === 'schema' ? renderSchemaHistory(isEnabled, snapshots, snapshotting, selectedSnapshot, setSelectedSnapshot, takeManualSnapshot, acknowledgeSnapshot) : null}
      </motion.div>
    </div>
  );
}

/* ── Overview tab ── */
function renderOverview(connection, monitorConfig, isEnabled, enabling, onToggle, onFreqChange) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="card-brutal p-5 space-y-3">
        <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-kova-slate">Details</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-kova-slate">Endpoint</span><span className="font-mono text-kova-silv text-xs truncate max-w-[60%]">{connection.base_url}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Method</span><span className="font-mono">{connection.method}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Auth</span><span className="font-mono">{connection.auth_type}</span></div>
          {connection.provider ? <div className="flex justify-between"><span className="text-kova-slate">Provider</span><span className="font-mono">{connection.provider}</span></div> : null}
          {connection.description ? <div className="pt-2 border-t border-kova-cyan/12"><p className="text-kova-slate text-xs">{connection.description}</p></div> : null}
        </div>
      </div>

      <div className="card-brutal p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-kova-slate">Schema Monitoring</h3>
          <span className={'text-[9px] font-mono px-2 py-0.5 uppercase tracking-wider ' + (isEnabled ? 'bg-kova-green/10 text-kova-green border border-kova-green/20' : 'bg-white/4 text-kova-slate border border-kova-cyan/12')}>
            {isEnabled ? 'Active' : 'Inactive'}
          </span>
        </div>
        <p className="text-[10px] text-kova-slate">Detect upstream API schema changes before they break integrations. Only field paths and types are stored — never actual response data.</p>
        <div className="flex items-center justify-between pt-2 border-t border-kova-cyan/12">
          <Label className="text-xs">Enable monitoring</Label>
          <Switch checked={isEnabled || false} onCheckedChange={onToggle} disabled={enabling} data-testid="schema-monitor-toggle" />
        </div>
        {isEnabled ? (
          <div className="space-y-2 pt-2">
            <Label className="text-[10px] text-kova-slate">Check frequency</Label>
            <div className="flex gap-1.5 flex-wrap">
              {FREQ_OPTIONS.map(function (opt) {
                var active = monitorConfig && monitorConfig.frequency === opt.value;
                return (
                  <button key={opt.value} onClick={function () { onFreqChange(opt.value); }} className={'text-[10px] font-mono px-2 py-1 border transition-colors ' + (active ? 'bg-kova-cyan/15 text-kova-cyan border-kova-cyan/30' : 'text-kova-slate border-kova-cyan/12 hover:text-kova-silv')} data-testid={'freq-' + opt.value}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ── Schema history tab ── */
function renderSchemaHistory(isEnabled, snapshots, snapshotting, selectedSnapshot, setSelectedSnapshot, onTakeSnapshot, onAcknowledge) {
  return (
    <div className="space-y-4" data-testid="schema-history-tab">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">{snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''}</span>
        <Button onClick={onTakeSnapshot} disabled={snapshotting || !isEnabled} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[10px] tracking-wider uppercase" data-testid="manual-snapshot-btn">
          {snapshotting ? 'Taking...' : 'Take Snapshot Now'}
        </Button>
      </div>

      {!isEnabled ? (
        <div className="card-brutal p-12 text-center">
          <ShieldCheck size={40} className="text-kova-cyan/12 mx-auto mb-3" />
          <p className="text-kova-slate font-mono text-sm mb-1">Schema monitoring is not enabled</p>
          <p className="text-[10px] text-kova-slate">Enable it in the Overview tab to start tracking schema changes</p>
        </div>
      ) : snapshots.length === 0 ? (
        <div className="card-brutal p-12 text-center">
          <p className="text-kova-slate font-mono text-sm">No snapshots yet</p>
          <p className="text-[10px] text-kova-slate mt-1">Take a manual snapshot or wait for the next scheduled check</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          {/* Timeline */}
          <div className="card-brutal divide-y divide-kova-cyan/12 overflow-hidden max-h-[600px] overflow-y-auto" data-testid="snapshot-timeline">
            {snapshots.map(function (snap) {
              var isSelected = selectedSnapshot && selectedSnapshot.snapshot_id === snap.snapshot_id;
              var hasBreaking = snap.has_breaking_changes && !snap.acknowledged_by;
              return (
                <button key={snap.snapshot_id} onClick={function () { setSelectedSnapshot(snap); }} className={'w-full text-left px-3 py-2.5 transition-colors ' + (isSelected ? 'bg-kova-cyan/8 border-l-2 border-l-kova-cyan' : 'hover:bg-kova-card/50')} data-testid={'snapshot-' + snap.snapshot_id}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-kova-silv">{formatTs(snap.captured_at)}</span>
                    {hasBreaking ? <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 bg-kova-red/15 text-kova-red border border-kova-red/30 uppercase">Breaking</span> : null}
                    {snap.acknowledged_by ? <CheckCircle size={12} className="text-kova-green" weight="fill" /> : null}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] font-mono text-kova-slate">{snap.snapshot_type}</span>
                    {snap.diff_from_prev ? <span className="text-[9px] font-mono text-kova-slate">{snap.diff_from_prev.total_changes} change{snap.diff_from_prev.total_changes !== 1 ? 's' : ''}</span> : null}
                    <span className="text-[9px] font-mono text-kova-slate">HTTP {snap.http_status}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="card-brutal overflow-hidden">
            {!selectedSnapshot ? (
              <div className="p-8 text-center text-kova-slate font-mono text-sm">Select a snapshot to view details</div>
            ) : renderSnapshotDetail(selectedSnapshot, onAcknowledge)}
          </div>
        </div>
      )}
    </div>
  );
}

function renderSnapshotDetail(snapshot, onAcknowledge) {
  var diff = snapshot.diff_from_prev;
  return (
    <div>
      <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center justify-between bg-kova-card">
        <div>
          <div className="text-xs font-mono text-kova-silv">{formatTs(snapshot.captured_at)}</div>
          <div className="text-[9px] font-mono text-kova-slate mt-0.5">{snapshot.snapshot_type} · HTTP {snapshot.http_status} · {snapshot.endpoint}</div>
        </div>
        {snapshot.has_breaking_changes && !snapshot.acknowledged_by ? (
          <Button onClick={function () { onAcknowledge(snapshot.snapshot_id); }} size="sm" variant="outline" className="border-kova-red/30 text-kova-red hover:bg-kova-red/10 rounded-none font-mono text-[10px] tracking-wider uppercase" data-testid={'ack-btn-' + snapshot.snapshot_id}>
            Acknowledge
          </Button>
        ) : null}
        {snapshot.acknowledged_by ? (
          <span className="text-[10px] font-mono text-kova-green flex items-center gap-1"><CheckCircle size={12} weight="fill" />Acknowledged</span>
        ) : null}
      </div>

      {diff ? (
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Schema Diff</span>
            {diff.has_breaking_changes ? (
              <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 bg-kova-red/15 text-kova-red border border-kova-red/30 uppercase tracking-wider">Breaking Changes</span>
            ) : null}
            <span className="text-[9px] font-mono text-kova-slate ml-auto">
              +{(diff.added_fields || []).length} added · -{(diff.removed_fields || []).length} removed · ~{(diff.type_changes || []).length} type
            </span>
          </div>
          <DiffViewer diff={diff} />
        </div>
      ) : (
        <div className="p-4">
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Baseline snapshot — no previous version to diff</span>
        </div>
      )}

      {snapshot.schema ? (
        <div className="p-4 border-t border-kova-cyan/12">
          <div className="text-[10px] font-mono text-kova-slate uppercase tracking-wider mb-2">Schema structure (values scrubbed)</div>
          <div className="bg-kova-bg border border-kova-cyan/12 p-3 max-h-[300px] overflow-auto">
            <SchemaTreeView schema={snapshot.schema} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
